import { useCallback, useEffect, useMemo, useState } from 'react';
import * as crmApi from '../utils/crmApi';

const DEFAULT_FILTERS = {
    q: '',
    status: '',
    type: '',
    city: '',
    minPrice: '',
    maxPrice: '',
    minArea: '',
    maxArea: '',
    tags: '',
    sort: 'createdAt',
    dir: 'desc',
    page: 1,
    pageSize: 24,
};

export function usePropertiesList(initialFilters = {}) {
    const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...initialFilters });
    const [searchInput, setSearchInput] = useState(initialFilters.q || '');
    const [properties, setProperties] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setFilters((prev) => ({ ...prev, q: searchInput, page: 1 }));
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchInput]);

    const fetchProperties = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await crmApi.getProperties(filters);
            setProperties(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to fetch properties');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchProperties();
    }, [fetchProperties]);

    const setFilter = useCallback((key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            page: key === 'page' ? value : 1,
        }));
    }, []);

    const clearFilters = useCallback(() => {
        setSearchInput('');
        setFilters({ ...DEFAULT_FILTERS });
    }, []);

    const activeFilters = useMemo(() => {
        const chips = [];
        if (filters.q) chips.push({ key: 'q', label: `Search: ${filters.q}` });
        if (filters.status) chips.push({ key: 'status', label: `Status: ${filters.status}` });
        if (filters.type) chips.push({ key: 'type', label: `Type: ${filters.type}` });
        if (filters.city) chips.push({ key: 'city', label: `City: ${filters.city}` });
        if (filters.minPrice) chips.push({ key: 'minPrice', label: `Min Price: ₹${filters.minPrice}` });
        if (filters.maxPrice) chips.push({ key: 'maxPrice', label: `Max Price: ₹${filters.maxPrice}` });
        if (filters.minArea) chips.push({ key: 'minArea', label: `Min Area: ${filters.minArea}` });
        if (filters.maxArea) chips.push({ key: 'maxArea', label: `Max Area: ${filters.maxArea}` });
        if (filters.tags) chips.push({ key: 'tags', label: `Tags: ${filters.tags}` });
        if (filters.sort && filters.sort !== 'createdAt') chips.push({ key: 'sort', label: `Sort: ${filters.sort}` });
        if (filters.dir && filters.dir !== 'desc') chips.push({ key: 'dir', label: `Dir: ${filters.dir}` });
        return chips;
    }, [filters]);

    return {
        filters,
        setFilters,
        setFilter,
        searchInput,
        setSearchInput,
        properties,
        total,
        loading,
        error,
        activeFilters,
        clearFilters,
        refetch: fetchProperties,
    };
}
